import { type EmojiId, type ExtendedEmoji } from '@app/client/common/structs';
import config from '@app/config';

import {
    ACTION_SET_CURRENT_EMOJI_TAGS,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_FILES,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_METADATA,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_NAME,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_URL,
    ACTION_SET_CURRENT_USER_EMOJI,
    ACTION_SET_CURRENT_USER_EMOJI_SEARCH,
    ACTION_SET_EDITING_EMOJI_TAGS,
    ACTION_SET_LOADING_EMOJI_TAGS,
    ACTION_SET_LOADING_EMOJIS,
    ACTION_SET_USER_EMOJIS,
    ACTION_SET_USER_EXTENDED_EMOJIS,
    ACTION_SET_USER_REMOVED_EMOJIS,
    type UserEmojiActionTypes,
    type UserEmojiState,
} from '../types/user-emojis';

const visualizerLink = config.visualizerLink;

export const LOCAL_STORAGE_CURRENT_EMOJI_KEY = 'currentEmojiId';

const DEFAULT: UserEmojiState = {
    emojis: [],
    extendedEmojis: [],
    currentEmoji: '',
    currentEmojiChainFormat: null,
    currentEmojiMinted: false,
    currentEmojiSearchResponse: null,
    currentEmojiTokenId: null,
    currentFree: false,
    currentRhythmScore: 0,
    currentGeneration: 0,
    tags: [],
    editingTags: [],
    loadingEmojis: false,
    loadingEmojiTags: false,
    blockedUntil: null,
    visualizerFiles: null,
    visualizerName: null,
    visualizerMetadata: null,
    visualizerUrl: '',
    visualizerIsLocked: null,
    visualizerNameIsLocked: null,
    removedEmojis: [],
};

// TODO: We need to figure out what this function is supposed to accomplish
// and why we are reaching for props that do not belong to `ExtendedEmoji`.
function getCurrentEmojiData(currentEmojiId: EmojiId, extendedEmojis: ExtendedEmoji[]): Partial<ExtendedEmoji> {
    return extendedEmojis.find((e) => e.display_format === currentEmojiId) || {};
}

export function userEmojis(state: UserEmojiState = DEFAULT, action: UserEmojiActionTypes): UserEmojiState {
    switch (action.type) {
        case ACTION_SET_LOADING_EMOJIS:
            return { ...state, loadingEmojis: action.loadingEmojis };
        case ACTION_SET_LOADING_EMOJI_TAGS:
            return { ...state, loadingEmojiTags: action.loadingEmojiTags };
        case ACTION_SET_USER_EMOJIS:
            return { ...state, emojis: action.emojis };
        case ACTION_SET_CURRENT_USER_EMOJI: {
            localStorage.setItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY, action.currentEmoji);
            const currentRhythmScore = getCurrentEmojiData(action.currentEmoji, state.extendedEmojis).rhythm_score ?? 0;
            const blockedUntil = getCurrentEmojiData(action.currentEmoji, state.extendedEmojis).blocked_until ?? null;
            const currentEmojiChainFormat =
                getCurrentEmojiData(state.currentEmoji, state.extendedEmojis).chain_format ?? null;
            const currentEmojiMinted = getCurrentEmojiData(action.currentEmoji, state.extendedEmojis).minted ?? false;
            const currentEmojiTokenId = getCurrentEmojiData(action.currentEmoji, state.extendedEmojis).token_id ?? null;
            const currentGeneration = getCurrentEmojiData(action.currentEmoji, state.extendedEmojis).generation;
            const currentFree = getCurrentEmojiData(action.currentEmoji, state.extendedEmojis).free ?? false;

            return {
                ...state,
                currentEmoji: action.currentEmoji,
                currentEmojiChainFormat,
                currentEmojiMinted,
                currentEmojiTokenId,
                currentRhythmScore,
                // TODO: Required because this type must be a `number`,
                // and the above `getCurrentEmojiData` could return `undefined`.
                ...(currentGeneration !== undefined ? { currentGeneration } : {}),
                currentFree,
                tags: [],
                blockedUntil,
            };
        }
        case ACTION_SET_USER_EXTENDED_EMOJIS: {
            const updateCurrentEmoji =
                action.emojis.length && !state.currentEmoji && !localStorage.getItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY);

            return {
                ...state,
                emojis: action.emojis.map((e) => e.display_format),
                currentEmoji: updateCurrentEmoji ? action.emojis[0].display_format : state.currentEmoji,
                extendedEmojis: action.emojis,
            };
        }
        case ACTION_SET_EDITING_EMOJI_TAGS:
            return { ...state, editingTags: [...action.tags] };
        case ACTION_SET_CURRENT_EMOJI_TAGS: {
            return {
                ...state,
                tags: [...action.tags],
            };
        }
        case ACTION_SET_CURRENT_EMOJI_VISUALIZER_NAME:
            return {
                ...state,
                visualizerName: action.visualizerName,
                visualizerNameIsLocked: action?.visualizerNameIsLocked ?? null,
            };
        case ACTION_SET_CURRENT_EMOJI_VISUALIZER_METADATA:
            return {
                ...state,
                visualizerMetadata: action.visualizerMetadata,
                visualizerIsLocked: action?.visualizerIsLocked ?? null,
            };
        case ACTION_SET_CURRENT_EMOJI_VISUALIZER_FILES:
            return { ...state, visualizerFiles: action.visualizerFiles };
        case ACTION_SET_CURRENT_EMOJI_VISUALIZER_URL: {
            const visualizerUrl = `${visualizerLink}?YAT=${action.currentEmoji}`;
            return { ...state, visualizerUrl };
        }
        case ACTION_SET_CURRENT_USER_EMOJI_SEARCH:
            return { ...state, currentEmojiSearchResponse: action.currentEmojiSearchResponse };
        case ACTION_SET_USER_REMOVED_EMOJIS:
            return { ...state, removedEmojis: action.removedEmojis };
        default: {
            return updatedUserEmojisFromState(state);
        }
    }
}

function updatedUserEmojisFromState(state: UserEmojiState = DEFAULT): UserEmojiState {
    const currentEmojiId = state.currentEmoji || localStorage.getItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY) || '';

    let newCurrentEmoji: EmojiId = '';
    let blockedUntil: UserEmojiState['blockedUntil'] = null;

    let currentEmojiChainFormat: UserEmojiState['currentEmojiChainFormat'] = null;
    let currentEmojiMinted: UserEmojiState['currentEmojiMinted'] = false;
    let currentEmojiTokenId: UserEmojiState['currentEmojiTokenId'] = null;
    let currentGeneration: UserEmojiState['currentGeneration'] | undefined = 0;
    let currentRhythmScore: UserEmojiState['currentRhythmScore'] = 0;

    if (state.emojis.indexOf(currentEmojiId) > -1) {
        newCurrentEmoji = currentEmojiId;

        blockedUntil = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).blocked_until ?? null; //todo - try undup some of this

        currentRhythmScore = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).rhythm_score ?? 0;
        currentEmojiChainFormat = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).chain_format ?? null;
        currentEmojiMinted = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).minted ?? false;
        currentEmojiTokenId = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).token_id ?? null;
        currentGeneration = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).generation;
    } else if (state.emojis.length > 0) {
        newCurrentEmoji = state.emojis[0];

        blockedUntil = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).blocked_until ?? null;

        currentRhythmScore = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).rhythm_score ?? 0;
        currentEmojiChainFormat = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).chain_format ?? null;
        currentEmojiMinted = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).minted ?? false;
        currentEmojiTokenId = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).token_id ?? null;
        currentGeneration = getCurrentEmojiData(newCurrentEmoji, state.extendedEmojis).generation;
    }

    return {
        ...state,
        blockedUntil,
        currentEmoji: newCurrentEmoji,
        currentEmojiChainFormat,
        currentEmojiMinted,
        currentEmojiTokenId,
        // TODO: Required because this type must be a `number`,
        // and the above `getCurrentEmojiData` could return `undefined`.
        ...(currentGeneration !== undefined ? { currentGeneration } : {}),
        currentRhythmScore,
    };
}
